import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Bell, Check, CheckCheck, Trash2, AlertTriangle, 
  Target, TrendingUp, Repeat, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const Notifications = () => {
  const { language } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'budget_warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'budget_exceeded':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'goal_achieved':
        return <Target className="w-5 h-5 text-emerald-500" />;
      case 'savings_milestone':
        return <TrendingUp className="w-5 h-5 text-emerald-500" />;
      case 'recurring_executed':
        return <Repeat className="w-5 h-5 text-blue-500" />;
      default:
        return <Bell className="w-5 h-5 text-slate-500" />;
    }
  };

  const getBgColor = (type, isRead) => {
    if (isRead) return 'bg-white';
    switch (type) {
      case 'budget_warning':
        return 'bg-amber-50';
      case 'budget_exceeded':
        return 'bg-red-50';
      case 'goal_achieved':
      case 'savings_milestone':
        return 'bg-emerald-50';
      default:
        return 'bg-slate-50';
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
            Notificaciones
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {unreadCount > 0 ? `${unreadCount} sin leer` : 'Todas leídas'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button 
            variant="outline"
            onClick={handleMarkAllRead}
            className="rounded-sm"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck size={16} className="mr-2" />
            Marcar todas como leídas
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <Card className="border-slate-200 rounded-sm shadow-none">
        {loading ? (
          <CardContent className="flex items-center justify-center h-48">
            <RefreshCw className="w-6 h-6 animate-spin text-emerald-900" />
          </CardContent>
        ) : notifications.length === 0 ? (
          <CardContent className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Bell size={32} className="mb-2" />
            <p>No hay notificaciones</p>
          </CardContent>
        ) : (
          <ScrollArea className="h-[600px]">
            <div className="divide-y divide-slate-100">
              {notifications.map(notif => (
                <div 
                  key={notif.id}
                  className={cn(
                    "flex items-start gap-4 p-4 transition-colors",
                    getBgColor(notif.type, notif.is_read)
                  )}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className="w-10 h-10 rounded-sm bg-white border border-slate-100 flex items-center justify-center flex-shrink-0">
                    {getIcon(notif.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn(
                        "font-medium text-slate-900",
                        !notif.is_read && "font-semibold"
                      )}>
                        {notif.title}
                      </p>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {formatDate(notif.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {notif.message}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notif.is_read && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleMarkRead(notif.id)}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-900"
                        title="Marcar como leída"
                      >
                        <Check size={16} />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(notif.id)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </Card>
    </div>
  );
};

export default Notifications;
